import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const idpConfigurationApi = {
  // Goal Status CRUD
  getAllGoalStatuses: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/goal-statuses`);
    } catch (error) {
      throw new ApiError('Failed to fetch goal statuses', error.status || 500);
    }
  },

  createGoalStatus: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/idp/goal-statuses`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid goal status data', 400);
      }
      throw new ApiError('Failed to create goal status', error.status || 500);
    }
  },

  updateGoalStatus: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/idp/goal-statuses/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid goal status data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Goal status not found', 404);
      }
      throw new ApiError('Failed to update goal status', error.status || 500);
    }
  },

  deleteGoalStatus: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/idp/goal-statuses/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Goal status not found', 404);
      }
      throw new ApiError('Failed to delete goal status', error.status || 500);
    }
  },

  // Goal Status Transitions CRUD
  getAllGoalStatusTransitions: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/goal-status-transitions`);
    } catch (error) {
      throw new ApiError('Failed to fetch goal status transitions', error.status || 500);
    }
  },

  createGoalStatusTransition: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/idp/goal-status-transitions`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid transition data', 400);
      }
      throw new ApiError('Failed to create goal status transition', error.status || 500);
    }
  },

  updateGoalStatusTransition: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/idp/goal-status-transitions/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid transition data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Goal status transition not found', 404);
      }
      throw new ApiError('Failed to update goal status transition', error.status || 500);
    }
  },

  deleteGoalStatusTransition: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/idp/goal-status-transitions/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Goal status transition not found', 404);
      }
      throw new ApiError('Failed to delete goal status transition', error.status || 500);
    }
  },

  // RAG Status CRUD
  getAllRAGStatuses: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/rag-statuses`);
    } catch (error) {
      throw new ApiError('Failed to fetch RAG statuses', error.status || 500);
    }
  },

  createRAGStatus: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/idp/rag-statuses`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid RAG status data', 400);
      }
      throw new ApiError('Failed to create RAG status', error.status || 500);
    }
  },

  updateRAGStatus: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/idp/rag-statuses/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid RAG status data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('RAG status not found', 404);
      }
      throw new ApiError('Failed to update RAG status', error.status || 500);
    }
  },

  deleteRAGStatus: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/idp/rag-statuses/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('RAG status not found', 404);
      }
      throw new ApiError('Failed to delete RAG status', error.status || 500);
    }
  },

  // Measurement Type CRUD
  getAllMeasurementTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/measurement-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch measurement types', error.status || 500);
    }
  },

  createMeasurementType: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/idp/measurement-types`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid measurement type data', 400);
      }
      throw new ApiError('Failed to create measurement type', error.status || 500);
    }
  },

  updateMeasurementType: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/idp/measurement-types/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid measurement type data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Measurement type not found', 404);
      }
      throw new ApiError('Failed to update measurement type', error.status || 500);
    }
  },

  deleteMeasurementType: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/idp/measurement-types/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Measurement type not found', 404);
      }
      throw new ApiError('Failed to delete measurement type', error.status || 500);
    }
  },

  // Resource Templates CRUD
  getAllResourceTemplates: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/resource-templates/all`);
    } catch (error) {
      throw new ApiError('Failed to fetch resource templates', error.status || 500);
    }
  },

  getPublishedResourceTemplates: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/resource-templates`);
    } catch (error) {
      throw new ApiError('Failed to fetch resource templates', error.status || 500);
    }
  },

  createResourceTemplate: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/idp/resource-templates`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid resource template data', 400);
      }
      throw new ApiError('Failed to create resource template', error.status || 500);
    }
  },

  updateResourceTemplate: async (id, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/idp/resource-templates/${id}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid resource template data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Resource template not found', 404);
      }
      throw new ApiError('Failed to update resource template', error.status || 500);
    }
  },

  deleteResourceTemplate: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/idp/resource-templates/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Resource template not found', 404);
      }
      throw new ApiError('Failed to delete resource template', error.status || 500);
    }
  },
};
